import { uInt8ArrayToHeightMap } from 'bin-3d-feed-volume';
import { colors } from '~/utility/BinSentry-ui-utility';
import { BinRenderer } from 'ui-3d-bin-readings';
import { SirenActionMixin, LitEntity, html, css, renderIf } from '../../utility/lit-entity';
import '../../common/bn-icon';
import '../../common/bn-banner';

class BinHeightMap extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.autoRotateSpeed = 5;
  }

  static get properties() {
    return {
      binLevelId: { type: String, attribute: 'bin-level-id' },
      heightMapId: { type: String },
      autoRotate: { type: Boolean, attribute: 'auto-rotate' },
      autoRotateSpeed: { type: Number, attribute: 'auto-rotate-speed' },
      userControl: { type: Boolean, attribute: 'user-control' },
      noHeightMap: { type: Boolean },
      medicated: { type: Boolean, attribute: 'medicated' },
      renderer: { type: Object },
      latestLabel: { type: String },
      showOutlierWarning: { type: Boolean, attribute: 'show-outlier-warning' },
      hideLegs: { type: Boolean, attribute: 'hide-legs' },
    };
  }

  static get styles() {
    return [css`
      :host {
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
      }
      .mount, bn-icon {
        width: 100%;
        height: 100%;
      }
      bn-icon {
        font-size: 1.5rem;
        text-align: center;
        transform: translateY(calc(50% - .75rem));
      }
      canvas {
        border-radius: var(--border-radius, 0px);
      }
      .hidden {
        display: none;
      }
      .outlier-banner {
        position: absolute;
        top: 0;
        left: 0.5rem;
        right: 0.5rem;
        z-index: 11;
      }
      .no-data.overlay {
        position: absolute;
        background: none;
        z-index: 10;
        height: 100%;
        width: 100%;
      }
      .no-data.overlay .background {
        position: absolute;
        opacity: .8;
        height: 100%;
        width: 100%;
        background-color: var(--gray-20);
      }
    `];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._reset();
  }

  render() {
    return html`
      ${renderIf(!this.renderer, html`<bn-icon name="fas fa-spinner fa-pulse"></bn-icon>`)}
      ${renderIf(this.renderer && this.noHeightMap, () => html`<slot></slot>`)}
      ${renderIf(this.latestLabel === 'OUTLIER' && this.showOutlierWarning, () => html`
        <bn-banner severity="warning" class="outlier-banner">
          <div>Recent Reading Not Available</div>
        </bn-banner>
        <div class="no-data overlay">
          <div class="background"></div>
        </div>
      `)}

      ${renderIf(this.binLevelId, () => html`
        <lit-entity .href="${this.binLevelId}" @update="${this._onUpdateBinLevel}"></lit-entity>
      `)}

      <lit-entity
        .href="${this.heightMapId}"
        @update="${this._onHeightMapEntity}"
      ></lit-entity>
      <div class="mount ${this.noHeightMap || !this.renderer ? 'hidden' : ''}"></div>
      <bin-latest-level .href="${this.href}" @levelUpdate="${this._onLevelUpdate}"></bin-latest-level>
    `;
  }

  _onLevelUpdate(e) {
    const { latestLabel } = e.detail;
    this.latestLabel = latestLabel;
  }

  updated(changedProperties) {
    if (changedProperties.get('href')) {
      this._reset();
    }
    if (changedProperties.has('medicated') && this.renderer) {
      this.renderer.colors = this.medicated ? {
        feedLight: colors.red[20],
        feedDark: colors.red[30],
      } : {};
    }
  }

  async _onUpdateBinLevel(e) {
    const { entity } = e.detail;
    this.binLevel = entity;
    if (!entity) {
      this.heightMapId = undefined;
      this.noHeightMap = true;
      return;
    }

    const heightMap = entity.getSubEntityByRel('https://api.binsentry.com/rel/height-map');
    this.heightMapId = heightMap && heightMap.href;
    this.noHeightMap = !this.heightMapId;
  }

  async _onHeightMapEntity(e) {
    const { entity } = e.detail;
    this.heightMap = entity;
    if (!entity) {
      return;
    }

    this.dispatchEvent(new CustomEvent('loaded-height-map', {
      detail: {
        heightMap: this.heightMap,
        binLevel: this.binLevel,
      },
      bubbles: true,
      composed: true,
    }));

    const dataHref = entity.getSubEntityByRel('https://api.binsentry.com/rel/height-map-data').href;
    this.rawHeightMapData = await this.fetchRaw(dataHref);
    if (!this.rawHeightMapData) {
      return;
    }

    const reader = this.rawHeightMapData.getReader();
    reader.read().then(({ value }) => {
      const { heightMap } = uInt8ArrayToHeightMap(value);
      this._onHeightMapData(heightMap);
    });
  }

  _onHeightMapData(heightMap) {
    if (!this.heightMap) {
      return;
    }

    if (this.renderer) {
      this.renderer.heightMap = heightMap;
      this.renderer.resolution = this.heightMap.properties.resolution;
      this.renderer.generate();
      return;
    }

    this.renderer = new BinRenderer({
      bin: this.entity.properties,
      heightMap,
      resolution: this.heightMap.properties.resolution,
      autoRotate: this.autoRotate,
      autoRotateSpeed: this.autoRotateSpeed,
      userControl: !!this.userControl,
      ...this.medicated ? { colors: {
        feedLight: colors.red[20],
        feedDark: colors.red[30],
      } } : {},
      hideLegs: this.hideLegs,
    });

    this.renderer.mount(this.shadowRoot.querySelector('.mount'));
    this.dispatchEvent(new CustomEvent('renderer-mounted', {
      detail: { renderer: this.renderer },
      bubbles: true,
      composed: true,
    }));
  }

  _reset() {
    if (this.renderer) {
      this.renderer.unmount();
      this.renderer = null;
    }
    this.heightMap = null;
    this.rawHeightMapData = null;
    this.noHeightMap = undefined;
    this.binLevelId = undefined;
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    if (!this.entity) {
      return;
    }
    this.binLevelId = this.binLevelId || this.getHref('https://api.binsentry.com/rel/bin-level-latest');
  }
}

window.customElements.define('bin-height-map', BinHeightMap);
